/**
 * Where Ready to schedule patients are (BAR-3107). The shipped stage rule moves a patient to
 * Pre-procedure on approval or first payment, so Ready to schedule, paid in full or approved with
 * nothing booked, sits in Pre-procedure, not Financing & approval, until Won. Tabs that show those
 * patients scope by the payment status as well as the stage. A Ready to schedule patient in
 * Post-procedure is missing their Won and is left out.
 */

import {
  BariatricProcedureStage,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  PAYMENT_STATUS_TAG_SYSTEMS,
} from '@bariendo/lib/constants';
import { PaymentStatus } from '@bariendo/lib/payment-status';
import { Meta } from '@medplum/fhirtypes';
import { FINANCING_STAGE_TAG } from './financingFilters';

const PRE_PROCEDURE_STAGE_TAG = `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.PRE_PROCEDURE}`;
const READY_TAG = `${PAYMENT_STATUS_TAG_SYSTEMS.status}|${PaymentStatus.READY_TO_SCHEDULE}`;

const tagClause = (tag: string): string => `_tag eq "${tag}"`;

/** To schedule: Ready to schedule, in Financing & approval or Pre-procedure. */
export const READY_TO_SCHEDULE_CLAUSE = `(${tagClause(READY_TAG)} and (${tagClause(FINANCING_STAGE_TAG)} or ${tagClause(PRE_PROCEDURE_STAGE_TAG)}))`;

/** A Coverage tab's case: in Financing & approval, or Ready to schedule in Pre-procedure. */
export const OPEN_CASE_CLAUSE = `(${tagClause(FINANCING_STAGE_TAG)} or (${tagClause(PRE_PROCEDURE_STAGE_TAG)} and ${tagClause(READY_TAG)}))`;

function has(patient: { meta?: Meta } | undefined, tag: string): boolean {
  const [system, code] = tag.split('|');
  return (patient?.meta?.tag ?? []).some((candidate) => candidate.system === system && candidate.code === code);
}

/** For rows that arrive by subscription, as `READY_TO_SCHEDULE_CLAUSE`. */
export function isReadyToSchedule(patient: { meta?: Meta } | undefined): boolean {
  return has(patient, READY_TAG) && (has(patient, FINANCING_STAGE_TAG) || has(patient, PRE_PROCEDURE_STAGE_TAG));
}

/** For rows that arrive by subscription, as `OPEN_CASE_CLAUSE`. */
export function isOpenCase(patient: { meta?: Meta } | undefined): boolean {
  return has(patient, FINANCING_STAGE_TAG) || (has(patient, PRE_PROCEDURE_STAGE_TAG) && has(patient, READY_TAG));
}

/** Plain parameters (a `_filter` criteria never fires): both stages, any of which can hold a row. */
export function readyToScheduleSubscriptionParams(): Record<string, string> {
  return { _tag: `${FINANCING_STAGE_TAG},${PRE_PROCEDURE_STAGE_TAG}` };
}
